import type { Position } from 'geojson';
import type { CustomArea } from '../types/domain';
import { CUSTOM_AREA_TYPES } from '../constants/customArea';

const isNotNull = <T>(value: T | null): value is T => {
  return value !== null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const parseAsNumber = (value: unknown): number | null => {
  return typeof value === 'string' &&
    value.trim() !== '' &&
    !Number.isNaN(Number(value))
    ? Number(value)
    : null;
};

const parseAsEnum = <T>(
  value: unknown,
  enums: T[] | readonly T[]
): T | null => {
  return enums.includes(value as T) ? (value as T) : null;
};

const parseAsObject = (value: unknown): Record<string, unknown> => {
  return isRecord(value) ? value : {};
};

const parseAsStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item: unknown): item is string => {
    return typeof item === 'string';
  });
};

const parseAsNumberArray = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(parseAsNumber).filter(isNotNull);
};

const parseAsEnumArray = <T>(
  value: unknown,
  enums: T[] | readonly T[]
): T[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item: unknown): T | null => {
      return parseAsEnum(item, enums);
    })
    .filter(isNotNull);
};

const parseAsRange = (value: unknown): [number, number] | null => {
  const isNested = Array.isArray(value) && Array.isArray(value[0]);
  const range = parseAsNumberArray(isNested ? value[0] : value);
  return range.length === 2 ? [range[0], range[1]] : null;
};

const parseAsPosition = (value: unknown): Position | null => {
  const position = parseAsNumberArray(value);
  return position.length === 2 ? position : null;
};

const parseAsPositions = (value: unknown): Position[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  const positions = value.map(parseAsPosition);
  return positions.every(isNotNull) ? positions : null;
};

const parseAsCoordinates = (value: unknown): Position[][] => {
  const polygons = (
    Array.isArray(value) ? value.filter(Array.isArray) : []
  ).map(parseAsPositions);
  return polygons.every(isNotNull) ? polygons : [];
};

const parseCustomAreas = (value: unknown): CustomArea[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item: unknown): CustomArea[] => {
    const customArea = parseAsObject(item);
    const coordinates = parseAsPositions(
      parseAsObject(customArea.geometry).coordinates
    );
    if (coordinates === null) {
      return [];
    }
    return [
      {
        key: String(customArea.key ?? ''),
        type: parseAsEnum(customArea.type, CUSTOM_AREA_TYPES) ?? 'polygon',
        label: String(customArea.label ?? ''),
        radius: parseAsNumber(customArea.radius) ?? 0,
        minutes: parseAsNumber(customArea.minutes) ?? 0,
        center: parseAsPosition(customArea.center) ?? [0, 0],
        hide: customArea.hide === 'true',
        geometry: { coordinates },
      },
    ];
  });
};

export {
  parseAsNumber,
  parseAsEnum,
  parseAsObject,
  parseAsStringArray,
  parseAsNumberArray,
  parseAsEnumArray,
  parseAsRange,
  parseAsCoordinates,
  parseCustomAreas,
};
